import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { CLIENT_GROUPS_QUERY_KEY } from '@/hooks/useClientGroups';
import {
  CreatePropertyRequestData,
  PropertyRequest,
  createPropertyRequest,
  getPropertyRequestsByGroup,
  resolvePropertyRequest,
} from '@/services/api/propertyRequests';

export const PROPERTY_REQUESTS_QUERY_KEY = ['property', 'requests'];

// ─── Query hook ────────────────────────────────────────────────────────────────

/**
 * Fetch pending property requests for multiple client groups.
 * Used by advisors to see buyer requests across all their groups.
 */
export const usePropertyRequests = (groupIds: string[]) => {
  const query = useQuery({
    queryKey: [...PROPERTY_REQUESTS_QUERY_KEY, ...groupIds],
    queryFn: async () => {
      const results = await Promise.all(groupIds.map((id) => getPropertyRequestsByGroup(id)));
      return results.flatMap((r) => r.requests);
    },
    enabled: groupIds.length > 0,
    staleTime: 15 * 1000,
  });

  return {
    requests: query.data ?? [],
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  };
};

/**
 * Get property requests grouped by groupId for easy lookup.
 */
export const usePropertyRequestsByGroup = (groupIds: string[]) => {
  const { requests, isLoading, error, refetch } = usePropertyRequests(groupIds);

  const byGroup = requests.reduce<Record<string, PropertyRequest[]>>((acc, req) => {
    if (!acc[req.groupId]) acc[req.groupId] = [];
    acc[req.groupId].push(req);
    return acc;
  }, {});

  return { requestsByGroup: byGroup, isLoading, error, refetch };
};

// ─── Mutation hooks ────────────────────────────────────────────────────────────

export const useCreatePropertyRequest = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreatePropertyRequestData) => createPropertyRequest(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PROPERTY_REQUESTS_QUERY_KEY });
    },
  });
};

export const useResolvePropertyRequest = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, action }: { id: string; action: 'approve' | 'reject' }) =>
      resolvePropertyRequest(id, action),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PROPERTY_REQUESTS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: CLIENT_GROUPS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: ['properties'] });
    },
  });
};
